import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { departureApi } from "@/lib/phase3Entities";
import { apiError } from "@/lib/errors";
import { CheckCircle2, Circle, ExternalLink } from "lucide-react";

const GROUP_LABELS = {
  client_contract: "Client & Contract",
  routing: "Routing",
  coach: "Coach",
  driver: "Driver",
  trailer: "Trailer",
  final: "Final Checks",
};
const GROUP_ORDER = ["client_contract", "routing", "coach", "driver", "trailer", "final"];

export default function DepartureTab({ tourId, canWrite = true, drivers = [] }) {
  const [items, setItems] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [notesOpen, setNotesOpen] = useState({});
  const navigate = useNavigate();
  const { id: tourIdFromUrl } = useParams();
  const resolvedTourId = tourId || tourIdFromUrl;

  // Resolve primary driver assignments for deep-linking
  const primaries = useMemo(() => (drivers || []).filter((d) => d.role === "primary_driver"), [drivers]);

  const load = async () => {
    try {
      const cl = await departureApi.get(tourId);
      setItems(cl.items || []);
      setReadiness(await departureApi.readiness(tourId));
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tourId]);

  const toggle = async (item) => {
    try {
      await departureApi.patchItem(tourId, item.id, { checked: !item.checked });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveNotes = async (item, notes) => {
    try {
      await departureApi.patchItem(tourId, item.id, { notes });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const byGroup = useMemo(() => {
    const out = {};
    for (const g of GROUP_ORDER) out[g] = [];
    for (const it of items) (out[it.category] = out[it.category] || []).push(it);
    return out;
  }, [items]);

  const percent = readiness?.percent || 0;
  const requiredRemaining = readiness?.required_remaining || 0;
  const readyColor = percent === 100 ? "bg-touring-lime" : requiredRemaining > 0 ? "bg-amber-hold" : "bg-coach-blue";
  const ringGradient = percent === 100
    ? "conic-gradient(#B7E31D 100%, #E8EBED 0)"
    : requiredRemaining > 0
      ? `conic-gradient(#D79A27 ${percent}%, #E8EBED 0)`
      : `conic-gradient(#1B4F8A ${percent}%, #E8EBED 0)`;

  return (
    <div className="space-y-4 min-w-0" data-testid="departure-tab">
      <div className="sticky top-0 z-10 bg-white border border-coach-silver rounded-md p-4 shadow-sm" data-testid="departure-readiness">
        <div className="flex items-center gap-5">
          {/* Readiness ring */}
          <div className="relative shrink-0" style={{ width: 88, height: 88 }} aria-hidden>
            <div
              className="w-full h-full rounded-full"
              style={{ background: ringGradient }}
            />
            <div className="absolute inset-[8px] bg-white rounded-full grid place-items-center">
              <div className="text-center">
                <div className="font-display text-xl text-deep-road-navy leading-none" data-testid="departure-percent">
                  {percent}%
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-steel">Departure Readiness</div>
            <div className="text-[15px] text-carbon font-medium mt-0.5">
              {percent === 100 ? "Ready to dispatch" : requiredRemaining > 0 ? "Required items remaining" : "All required complete"}
            </div>
            <div className="text-[12px] text-steel mt-1">
              {readiness?.checked}/{readiness?.total} checked ·{" "}
              <span className={requiredRemaining ? "text-amber-hold font-medium" : "text-touring-lime font-medium"}>
                {requiredRemaining === 0 ? "0 required remaining" : `${requiredRemaining} required item(s) remaining`}
              </span>
            </div>
          </div>
          <div className="flex-1 hidden md:block">
            <div className="h-3 bg-chrome-mist rounded-sm overflow-hidden">
              <div className={`h-full ${readyColor} transition-all`} style={{ width: `${percent}%` }} />
            </div>
          </div>
        </div>
      </div>

      {GROUP_ORDER.map((g) => {
        const groupItems = byGroup[g] || [];
        if (groupItems.length === 0) return null;
        return (
          <div key={g} className="bg-white border border-coach-silver rounded-md" data-testid={`departure-group-${g}`}>
            <div className="px-4 py-2 text-[10.5px] uppercase tracking-widest text-steel border-b border-coach-silver/60">
              {GROUP_LABELS[g]}
            </div>
            <ul>
              {groupItems.map((it) => {
                const notesActive = notesOpen[it.id];
                // Deep-link for pickup sheet related items
                const isPickupItem = /pickup sheet/i.test(it.label);
                const firstPrimary = isPickupItem ? primaries[0] : null;
                return (
                  <li key={it.id} data-testid={`departure-item-${it.id}`} className="border-b border-coach-silver/60 last:border-0">
                    <div className="flex items-start gap-3 px-4 py-2">
                      <button onClick={() => canWrite && toggle(it)} disabled={!canWrite}
                        className="mt-0.5 shrink-0" data-testid={`departure-toggle-${it.id}`}>
                        {it.checked
                          ? <CheckCircle2 size={20} className="text-touring-lime" />
                          : <Circle size={20} className={it.required ? "text-amber-hold" : "text-steel"} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[13.5px] ${it.checked ? "text-steel line-through" : "text-carbon"}`}>
                          {it.label}
                          {!it.required && <span className="ml-2 text-[10.5px] uppercase tracking-widest text-steel">optional</span>}
                        </div>
                        {it.checked_at && (
                          <div className="text-[10.5px] text-steel">
                            Checked {it.checked_at.replace("T", " ").slice(0, 16)} UTC
                          </div>
                        )}
                        {/* Pickup sheet deep-link per primary driver */}
                        {isPickupItem && primaries.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {primaries.map((p) => (
                              <button key={p.id} type="button"
                                data-testid={`departure-pickup-link-${p.id}`}
                                onClick={() => navigate(`/tours/${resolvedTourId}?tab=pickup&da=${p.id}`)}
                                className="inline-flex items-center gap-0.5 text-[11px] text-coach-blue hover:underline">
                                <ExternalLink size={10} /> View pickup sheet
                              </button>
                            ))}
                          </div>
                        )}
                        {(notesActive || it.notes) && (
                          <div className="mt-1">
                            <input defaultValue={it.notes || ""}
                              onBlur={(e) => { if (e.target.value !== (it.notes || "")) saveNotes(it, e.target.value); }}
                              placeholder="Notes…"
                              className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[12.5px]"
                              data-testid={`departure-notes-${it.id}`} />
                          </div>
                        )}
                      </div>
                      <button onClick={() => setNotesOpen({ ...notesOpen, [it.id]: !notesActive })}
                        className="text-[11px] text-steel hover:text-coach-blue"
                        data-testid={`departure-notes-toggle-${it.id}`}>
                        {notesActive ? "Hide" : "Notes"}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
